import React, {
    useCallback,
    useEffect,
} from 'react';

import { useDispatch, useSelector } from 'react-redux';

import qs from 'querystring';

import { Card, Disclaimer, Sheet } from '@iris/react-components';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { getAndSaveConsumerMetadata } from '@atc/consumer-metadata';
import { useFeatures } from '@atc/features';

import { Alert, AnimatedButton, Button, Image, Popup } from 'reaxl';
import {
    sendClick,
} from 'reaxl-analytics';
import {
    emailSubscribeSignIn,
    facebookSubscribeSignIn,
    sendSubscribeClick,
} from 'reaxl-analytics-handlers';
import { Form, Input, StandardEmailInput } from 'reaxl-forms';

import { subscribe } from 'axl-config';

import LocalStorage from '@/utilities/LocalStorage';

import remoteActions from '@/actions/srp/remoteActions';

import { sendCaptureSubscribeClick } from '@/analytics/srp/srpEmailCaptureClickEvents';

import authDuck from '@/ducks/authDuck.js';
import recentSearchDuck from '@/ducks/recentSearchDuck.js';
import srpActiveInteractionDuck from '@/ducks/srp/srpActiveInteractionDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpSortDuck from '@/ducks/srp/srpSortDuck.js';
import userDuck from '@/ducks/userDuck.js';

import emailRegistrationServiceTransformer from '@/transformers/emailRegistrationServiceTransformer.js';

export function EmailSubscribeContainer() {
    const dispatch = useDispatch();
    const {
        authentication: [, { sign_in_path: signInPath }],
        brand: [, { consumer_brand: consumerBrand }],
        personalization: [isPersonalizationEnabled] = [],
        SAVE_SEARCH_OFF: [saveSearchDisabled],
    } = useFeatures([
        'authentication',
        'brand',
        'personalization',
        'SAVE_SEARCH_OFF',
    ]);

    const isEmailCaptureModal = useSelector(srpActiveInteractionDuck.selectors.getShowEmailCaptureModal);

    const search = useSelector(userDuck.selectors.getSavedSearch);
    const subscribedSearch = useSelector(userDuck.selectors.getSubscribedSearch);
    const { saveSearchString } = search;
    const isLatestSearchSubscribed = useSelector((state) => _get(recentSearchDuck.selectors.getRecentSearches(state), '[0].isSubscribed'));
    const showEmailSubscribeInline = useSelector(userDuck.selectors.getShowEmailSubscribeInline);
    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);
    const sort = useSelector(srpSortDuck.selectors.getDuckState, _isEqual);
    const requestValuesWithoutSort = useSelector(srpFiltersDuck.selectors.getRequestValues, _isEqual);

    const isSearchSavable = !isLoggedIn && !(search.title?.length);
    const hasSearchParams = !!(requestValuesWithoutSort && Object.keys(requestValuesWithoutSort).length);
    const isInlineSaveSearchOff = saveSearchDisabled;
    const isInlineSaveSearchEnabled = !isInlineSaveSearchOff && !isLatestSearchSubscribed;
    const showEmailSubscribe = !isInlineSaveSearchOff && hasSearchParams && isSearchSavable && isInlineSaveSearchEnabled && showEmailSubscribeInline;
    const { animatedButtonState } = useSelector((state) => state.savedInventory) || {};
    const user = useSelector(userDuck.selectors.getDuckState);
    const {
        showEmailSubscribeSuccess,
        showEmailSubscribeFailure,
        lightRegistrationError,
        emailAddress = '',
    } = user;

    const showPrivacyStatement = !showEmailSubscribeSuccess;
    const saveSearchImage = '/cm-api/content/static/img/icon/myatc/save-search-illustration.png';

    const setEmailAlerts = useCallback(() => {
        const parsedQuery = qs.parse(saveSearchString.slice(1));
        const transformedQuery = emailRegistrationServiceTransformer.transformSearch(parsedQuery);
        const requestValues = {
            ...requestValuesWithoutSort,
            sortBy: sort,
        };
        dispatch(remoteActions.setEmailAlertsSearch({
            search: {
                ...transformedQuery,
                requestValues,
            },
            onLogInSuccess: (response) => {
                const {
                    provider,
                } = response;
                if (provider === 'facebook') {
                    sendClick(facebookSubscribeSignIn);
                } else {
                    sendClick(emailSubscribeSignIn);
                }
            },
        }));
    }, [dispatch, saveSearchString, requestValuesWithoutSort, sort]);

    const handleEmailSubscribeClose = () => {
        const Storage = LocalStorage.getInstance();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        Storage.useSession(true);
        Storage.set('emailSubscribe', 'closed');
        dispatch(userDuck.creators.showEmailSubscribeInline(false));
        dispatch(srpActiveInteractionDuck.creators.setKeys({ showEmailCaptureModal: false }));
    };

    const handleSubmit = async (event) => {
        await getAndSaveConsumerMetadata({
            email: event.emailAddress,
            brand: consumerBrand,
            shouldUpdateConsumerMetadata: true,
        });
        if (isEmailCaptureModal) {
            sendClick(sendSubscribeClick, event, {});
            dispatch(srpActiveInteractionDuck.creators.setKeys({ showEmailCaptureModal: false }));
        } else {
            sendClick(sendCaptureSubscribeClick, event, {});
        }
    };

    useEffect(() => {
        if (showEmailSubscribe) {
            setEmailAlerts();
        }
    }, [saveSearchString, setEmailAlerts, showEmailSubscribe]);

    const getCallbacks = () => {
        const {
            onLogInSuccess = () => { },
            onSaveSuccess = () => { },
        } = subscribedSearch;
        return {
            onLogInSuccess: (res) => {
                onLogInSuccess(res, search);
            },
            onSaveSuccess: (res) => {
                onSaveSuccess(res, search);
            },
        };
    };
    const handleRegister = (data) => {
        dispatch(userDuck.creators.registerEmailSubscribeUser({
            data: {
                email: data.emailAddress,
                search: subscribedSearch.saveSearchObject,
            },
            ...getCallbacks(),
        }));
        handleSubmit(data);
    };

    const renderEmailSubscribeFailure = () => (
        <>
            <div className="display-flex">
                <div className="margin-auto padding-bottom-3">
                    <Image
                        src={saveSearchImage}
                    />
                </div>
            </div>
            <Alert bsStyle="warning">
                You already have a maximum of 2 searches saved. To save this search, please create a free account.
            </Alert>
            <div>
                <span
                    className="text-bold"
                >
                    Create an account
                </span>
                {' '}
                {subscribe.inline.confirmation.signUp}
            </div>
        </>
    );

    const renderEmailSubscribeConfirmation = () => {
        if (showEmailSubscribeFailure) {
            return renderEmailSubscribeFailure();
        }
        return (
            <Alert bsStyle="success">
                <span className="text-bold margin-right-2">Thanks for subscribing!</span>
                {subscribe.confirmation.alert}
            </Alert>
        );
    };

    const renderEmailSubscribeForm = () => (
        <>
            <div className="row">
                <div className="col-xs-12 text-left display-flex">
                    <div className="margin-right-4">
                        <Image
                            src={saveSearchImage}
                            alt=""
                            lazyload={false}
                            loading="eager"
                            style={{ maxWidth: '76px', height: '55px' }}
                        />
                    </div>
                    <div className="text-bold text-size-400">{subscribe.message}</div>
                </div>
            </div>
            <Form
                onValidSubmit={handleRegister}
                className="margin-top-5"
            >
                <div className="row">
                    <div className="col-xs-7">
                        <StandardEmailInput
                            maxLength="45"
                            name="emailAddress"
                            label=""
                            required
                            placeholder="Email"
                            value={emailAddress}
                        />
                    </div>
                    <div className="col-xs-5">
                        <AnimatedButton
                            type="submit"
                            bsStyle="default"
                            block
                            formNoValidate
                            buttonText="Subscribe"
                            loadedText="Subscribed"
                            buttonState={animatedButtonState}
                        />
                    </div>
                </div>
                {lightRegistrationError && (
                    <Alert bsStyle="danger">{lightRegistrationError}</Alert>
                )}

                <Input
                    type="hidden"
                    name="textPot"
                    id="textPot"
                    validations={{
                        minLength: 0,
                    }}
                />

            </Form>
        </>
    );

    const getModalBody = () => {
        if (showEmailSubscribeSuccess || showEmailSubscribeFailure) {
            return renderEmailSubscribeConfirmation();
        }
        return renderEmailSubscribeForm();

    };

    const handleClose = () => {
        dispatch(userDuck.creators.hideEmailSubscribeModal());
        handleEmailSubscribeClose();
    };

    const navigateToSignIn = () => {
        const refUrl = new URL(globalThis.location.href);
        refUrl.pathname = signInPath;
        refUrl.search = new URLSearchParams({
            defaultScreen: 'signup',
            refUrl,
        });
        globalThis.location.assign(refUrl);
    };
    const handleSignUpSave = () => {
        navigateToSignIn();
    };

    const renderEmailSubscribeFailureFooter = () => (
        <div className="margin-left-auto padding-4">
            <Button
                className="margin-right-4"
                onClick={handleClose}
                bsStyle="text"
            >
                Cancel
            </Button>
            <Button
                type="submit"
                bsStyle="primary"
                onClick={handleSignUpSave}
            >
                Create an Account
            </Button>
        </div>
    );

    const getModalFooter = () => {
        if (showEmailSubscribeFailure) {
            return renderEmailSubscribeFailureFooter();
        }
        return (
            <Card
                aria-label="Card"
                variant="brand-subtle"
                className="iris:block"
            >
                <Button
                    className="margin-right-1"
                    onClick={handleSignUpSave}
                    bsStyle="text"
                >
                    Create an account
                </Button>
                <span>{subscribe.inline.confirmation.signUp}</span>
            </Card>
        );
    };

    const getPrivacyStatement = () => (
        <span>
            {subscribe.privacyStatement.text}
            <Popup
                width={600}
                height={400}
                url={subscribe.privacyStatement.href}
            >
                <span className="text-link margin-left-1">
                    {subscribe.privacyStatement.label}
                </span>
            </Popup>
            .
        </span>

    );

    if (isPersonalizationEnabled && showEmailSubscribe) {

        return (
            <div
                data-cmp="emailSubscribeContainer"
            >
                <Sheet
                    title={subscribe.title}
                    hasHeader
                    modal
                    desktopPosition="center"
                    aria-label="Email subscription for vehicle search alerts"
                    open={showEmailSubscribe}
                    onOpenChange={(isOpen) => {
                        if (!isOpen) {
                            handleEmailSubscribeClose();
                        }
                    }}
                    onOpenAutoFocus={(e) => {
                        e.preventDefault();
                    }}
                    bodyClassName="modal-sm"
                >
                    <div className="iris:w-full">
                        {getModalBody()}
                        {showPrivacyStatement && !showEmailSubscribeFailure && (
                            <Disclaimer>
                                {getPrivacyStatement()}
                            </Disclaimer>
                        )}
                        {getModalFooter()}
                    </div>
                </Sheet>
            </div>

        );
    }
    return null;
}
export default EmailSubscribeContainer;
